<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useFlash } from 'mu-vue-utils/Composables/flash';
import { WMModal } from '../../composables/WM';
import { useI18n } from '../../composables/I18n';
import { useTheme } from '../../composables/useTheme';
import { themeTL } from '../../generated/tl';
import { formatTlError, formatCommonResultErrors } from '../../services/tlError';
import { APPEARANCE_LIMITS, type ThemeColorMode, type ThemePublicDto } from '../../types/theme';
import Loader from '../Loader.vue';

/**
 * Osobní přizpůsobení vzhledu (docs/tasks/063-vzhled-2.md). Každá změna se hned aplikuje jako náhled;
 * "Uložit" ji uloží uživateli, zavření jinak vrátí původní nastavení.
 */
const props = defineProps<{ modal: WMModal }>();

const { t } = useI18n();
const { addFlash } = useFlash();
const { appearance, shownTheme, isDark, beginAppearanceEdit, previewAppearance, saveAppearance, revertAppearance } = useTheme();

const loading = ref(true);
const saving = ref(false);
let saved = false;
const defaultTheme = ref<ThemePublicDto | null>(null);
const selectable = ref<ThemePublicDto[]>([]);

onMounted(async () => {
  beginAppearanceEdit();
  try {
    const [def, list] = await Promise.all([themeTL.getActiveTheme(), themeTL.findSelectableThemes()]);
    defaultTheme.value = def;
    // výchozí vzhled se nabízí jako "null"; jeho případný druhý výskyt mezi vybíratelnými se vynechá
    selectable.value = list.filter(x => x.id !== def.id);
  } catch (e) {
    addFlash(formatTlError(e), 'danger');
  } finally {
    loading.value = false;
  }
});

// zavření bez uložení (křížek, Zrušit, odhlášení…) vrátí původní vzhled
onBeforeUnmount(() => { if (!saved) void revertAppearance(); });

const themeChoices = computed(() => defaultTheme.value ? [defaultTheme.value, ...selectable.value] : []);
const selectedThemeId = computed(() => appearance.value.themeId);
const pickTheme = (theme: ThemePublicDto) => {
  const isDefault = theme.id === defaultTheme.value?.id;
  previewAppearance({ themeId: isDefault ? null : theme.id }, theme);
};

const themeDensity = computed(() => shownTheme.value?.settings.density ?? 100);
const density = computed({
  get: () => appearance.value.density ?? themeDensity.value,
  set: (v: number) => previewAppearance({ density: Math.round(Number(v)) }),
});
const fontScale = computed({
  get: () => appearance.value.fontScale ?? 100,
  set: (v: number) => previewAppearance({ fontScale: Math.round(Number(v)) }),
});
const fontScaleLabel = computed(() => {
  const d = fontScale.value - 100;
  return d === 0 ? '100 %' : `${d > 0 ? '+' : '−'}${Math.abs(d)} %`;
});

const colorMode = computed<ThemeColorMode>({
  get: () => appearance.value.colorMode ?? (shownTheme.value?.settings.defaultColorMode ?? (isDark.value ? 'dark' : 'light')),
  set: (v) => previewAppearance({ colorMode: v }),
});

const resetToDefaults = () => {
  // null = hodnota z tématu; výchozí vzhled z administrace
  previewAppearance({ themeId: null, density: null, fontScale: null, colorMode: null }, defaultTheme.value);
};

const save = async () => {
  saving.value = true;
  try {
    const result = await saveAppearance();
    if (result.status === 'error') {
      addFlash(formatCommonResultErrors(result.errors), 'danger');
      return;
    }
    saved = true;
    addFlash(t('appearance.saved'), 'success');
    props.modal.close(undefined);
  } catch (e) {
    addFlash(formatTlError(e), 'danger');
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Loader v-if="loading" />
  <div v-else class="appearance-dialog">
    <p class="text-muted small">{{ t('appearance.hint') }}</p>

    <section v-if="themeChoices.length > 1" class="mb-3">
      <h2 class="h6">{{ t('appearance.theme') }}</h2>
      <label v-for="theme of themeChoices" :key="theme.id ?? 'default'" class="form-check">
        <input type="radio" class="form-check-input" name="appearanceTheme"
               :checked="(theme.id === defaultTheme?.id ? null : theme.id) === selectedThemeId" @change="pickTheme(theme)">
        <span class="form-check-label">
          {{ theme.name }}<span v-if="theme.id === defaultTheme?.id" class="text-muted"> ({{ t('appearance.defaultTheme') }})</span>
        </span>
      </label>
    </section>

    <section class="mb-3">
      <h2 class="h6">{{ t('appearance.mode') }}</h2>
      <div class="d-flex flex-wrap gap-3">
        <label v-for="mode of (['light', 'dark', 'system'] as const)" :key="mode" class="form-check">
          <input type="radio" class="form-check-input" name="appearanceMode" :value="mode" v-model="colorMode">
          <span class="form-check-label">{{ t(mode === 'light' ? 'theme.modeLight' : mode === 'dark' ? 'theme.modeDark' : 'theme.modeSystem') }}</span>
        </label>
      </div>
    </section>

    <section class="mb-3">
      <h2 class="h6">{{ t('appearance.density') }}: {{ density }} %</h2>
      <div class="appearance-dialog__range">
        <span class="small text-muted">{{ t('theme.densityCompact') }}</span>
        <input type="range" class="form-range" :min="APPEARANCE_LIMITS.density.min" :max="APPEARANCE_LIMITS.density.max" step="5" v-model.number="density">
        <span class="small text-muted">{{ t('theme.densityAiry') }}</span>
      </div>
    </section>

    <section class="mb-3">
      <h2 class="h6">{{ t('appearance.fontSize') }}: {{ fontScaleLabel }}</h2>
      <div class="appearance-dialog__range">
        <span class="small text-muted">A</span>
        <input type="range" class="form-range" :min="APPEARANCE_LIMITS.fontScale.min" :max="APPEARANCE_LIMITS.fontScale.max" step="5" v-model.number="fontScale">
        <span class="text-muted">A</span>
      </div>
    </section>

    <div class="d-flex gap-2 mt-4">
      <button type="button" class="btn btn-primary" :disabled="saving" @click="save">{{ t('common.save') }}</button>
      <button type="button" class="btn btn-outline-secondary" @click="resetToDefaults">{{ t('appearance.reset') }}</button>
      <button type="button" class="btn btn-secondary ms-auto" @click="props.modal.close(undefined)">{{ t('common.cancel') }}</button>
    </div>
  </div>
</template>
